import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * The registration form needs a B2C account: the request is keyed by the applicant's
 * object id and the bank list requires a token. Send anyone without a session through
 * the sign-up policy first, remembering the requested role across the redirect.
 */
export const b2cSignupGuard: CanActivateFn = async (route) => {
    const msalService = inject(MsalService);

    // Impersonation stands in for the B2C session; there is nothing to sign in to
    if (environment.devAuth?.enabled) {
        return true;
    }

    // Drive initialization here rather than waiting on inProgress$: this route is not
    // behind MsalGuard, so nothing else processes the redirect and the status would
    // stay at Startup forever.
    try {
        await firstValueFrom(msalService.initialize());
        await firstValueFrom(msalService.handleRedirectObservable());
    } catch (error) {
        console.error('Failed to initialize authentication', error);
    }

    if (msalService.instance.getAllAccounts().length > 0) {
        return true;
    }

    // The sign-up redirect returns to '/', which drops the query string
    sessionStorage.setItem('pendingRegistrationRole', route.queryParamMap.get('role') ?? '');

    const signUpRequest: RedirectRequest = {
        authority: environment.b2cPolicies.authorities.signUp.authority,
        scopes: environment.apiConfig.scopes,
        redirectUri: '/',
    };
    msalService.loginRedirect(signUpRequest);

    return false;
};
